import React from "react";
import styled from "styled-components";
import Tile from "../Tile";
import { spriteKey } from "../../engine/solitaire";

// The visual stack has 1..4 tiles behind the top card to suggest depth as
// the foundation fills. Exported so WinCelebration can locate the top
// card's corner in canvas space for the launch animation.
export const TRANSLATE_DOWN = 1;
export const TRANSLATE_RIGHT = 2;

export function numberOfCardsInStack(size) {
  if (size <= 4) return 1; // ace-4
  if (size <= 8) return 2; // 5-8
  if (size <= 12) return 3; // 9-queen
  return 4; // king
}

// A single foundation slot: the empty-foundation placeholder when the
// stack is empty, otherwise the top card atop 0..3 shadow tiles that hint
// at depth. onClick + selected apply to the topmost tile only.
function Foundation({ stack, onClick, selected = false, sourceTag }) {
  // Always render the same Wrapper so the empty→stacked transition can't
  // change the slot's box structure.
  if (stack.length === 0) {
    return (
      <Wrapper>
        <Tile
          name="foundation"
          style={{ gridArea: "area" }}
          {...(onClick ? { as: "button", onClick } : {})}
          selected={selected}
        />
      </Wrapper>
    );
  }
  const topCard = spriteKey(stack[stack.length - 1]);
  const numberOfCards = numberOfCardsInStack(stack.length);
  // The card directly under the top, revealed whenever the top is lifted
  // mid-drag (yours or a spectated player's) — without it, dragging a 2
  // hid the ace and dragging a lone ace left a hole in the felt.
  const underCard =
    stack.length >= 2 ? spriteKey(stack[stack.length - 2]) : null;
  const underIndex = Math.max(0, numberOfCardsInStack(stack.length - 1) - 1);
  return (
    <Wrapper>
      <Tile name="foundation" style={{ gridArea: "area" }} aria-hidden="true" />
      {underCard && (
        <Tile
          name={underCard}
          aria-hidden="true"
          style={{
            gridArea: "area",
            transform: `translate(${TRANSLATE_RIGHT * underIndex}px, ${TRANSLATE_DOWN * underIndex}px)`,
          }}
        />
      )}
      {Array.from({ length: numberOfCards }).map((_, index) => {
        const isTop = index === numberOfCards - 1;
        const style = {
          gridArea: "area",
          transform: `translate(${TRANSLATE_RIGHT * index}px, ${TRANSLATE_DOWN * index}px)`,
        };
        return (
          <Tile
            key={index}
            // Depth tiles wear the under-card so a lifted top reveals the
            // card actually beneath it, not a twin.
            name={isTop ? topCard : (underCard ?? topCard)}
            style={style}
            {...(isTop && onClick ? { as: "button", onClick } : {})}
            selected={isTop && selected}
            data-card-source={isTop && sourceTag ? sourceTag : undefined}
          />
        );
      })}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: grid;
  grid-template-areas: "area";
`;

export default Foundation;
